/**
 * If a Vue page exports `routes`, synthesize a `setupApp` that creates
 * vue-router with the right history mode, installs it on the app, and
 * navigates to the request URL — the boilerplate users would otherwise
 * write by hand. The wrapper is injected into the page's `<script>`
 * block so it uses the page bundle's own vue-router instance, which
 * matters because vue-router's provide/inject relies on shared module
 * symbols — a separate runtime-side router would mismatch the page's
 * `useRoute()` calls.
 *
 * If the user also exported their own `setupApp`, it's renamed to
 * `__absoluteUserSetupApp__` and called by the auto-wrapper after the
 * router is installed, with `router` added to its ctx for guards/etc.
 *
 * Shared between the build-time Vue compiler (compileVue.ts) and the
 * dev module server (moduleServer.ts) — both must apply this transform
 * so the synthesized setupApp is present in every served version of a
 * page module, not just the bundled one.
 */
const SCRIPT_REGEX =
	/(<script\b(?![^>]*\bsetup\b)[^>]*>)([\s\S]*?)(<\/script>)/;

const ROUTES_EXPORT_REGEX = /\bexport\s+(?:const|let|var)\s+routes\b/;

const SENTINEL = '__ABSOLUTE_AUTO_ROUTER__';

const SETUP_APP_DECLARATION_REGEX =
	/\bexport\s+(const|let|var)\s+setupApp\b/;

const SETUP_APP_FUNCTION_REGEX =
	/\bexport\s+(async\s+function|function)\s+setupApp\b/;

export const addAutoRouterSetupApp = (sourceContent: string) => {
	if (!ROUTES_EXPORT_REGEX.test(sourceContent)) return sourceContent;

	const match = SCRIPT_REGEX.exec(sourceContent);
	if (!match) return sourceContent;

	const [, openTag, scriptBody, closeTag] = match;
	if (!openTag || scriptBody === undefined || !closeTag) return sourceContent;

	// Idempotent — skip if already transformed.
	if (scriptBody.includes(SENTINEL)) return sourceContent;

	let rewrittenScript = scriptBody.replace(
		SETUP_APP_DECLARATION_REGEX,
		'const __absoluteUserSetupApp__'
	);
	rewrittenScript = rewrittenScript.replace(
		SETUP_APP_FUNCTION_REGEX,
		'$1 __absoluteUserSetupApp__'
	);
	const userHadSetupApp = rewrittenScript !== scriptBody;

	const autoWrapper = `
// ${SENTINEL} — generated by AbsoluteJS because this page exports
// \`routes\`. Owns the vue-router lifecycle (history pick, app.use,
// push, isReady) so user code stays declarative.
import {
	createRouter as __absoluteCreateRouter__,
	createMemoryHistory as __absoluteCreateMemoryHistory__,
	createWebHistory as __absoluteCreateWebHistory__,
} from 'vue-router';
${
	userHadSetupApp
		? ''
		: 'const __absoluteUserSetupApp__ = null;'
}
export const setupApp = async (app, ctx) => {
	const router = __absoluteCreateRouter__({
		history: ctx.isServer
			? __absoluteCreateMemoryHistory__()
			: __absoluteCreateWebHistory__(),
		routes,
	});
	app.use(router);
	if (ctx.isServer) {
		await router.push(ctx.url);
	}
	await router.isReady();
	if (__absoluteUserSetupApp__) {
		await __absoluteUserSetupApp__(app, { ...ctx, router });
	}
};
`;

	return sourceContent.replace(
		SCRIPT_REGEX,
		`${openTag}${rewrittenScript}${autoWrapper}${closeTag}`
	);
};
